import { Calculate, EditNote, KeyboardArrowLeft, KeyboardArrowRight, RotateLeft } from '@mui/icons-material';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Button, useMediaQuery, useTheme } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useCallback, useContext, useEffect } from 'react';
import TabPanel from '../../common/components/nav/TabPanel';
import FormProgressBar from '../components/loaders/FormProgressBar';
import TabContent from '../components/nav/TabContent';
import SaveCalcModal from '../components/popups/SaveCalcModal';
import { useTabPanels } from '../hooks/useTabPanels';
import IntroTab from '../pages/calculator-pages/IntroTab';
import { OverallDataContext, TabContext } from '../pages/CalculatorPage';

export default function CalculatorLayout() {
    // Data context
    const [calcStage, setStage] = useContext(OverallDataContext).calcStage;
    const [tabValue, setTabValue] = useContext(TabContext).tabValue;
    const setTagToTabMap = useContext(TabContext).tagToTabMap[1];

    // UI Flags
    const formID = useContext(OverallDataContext).formID;
    const isLoading = useContext(OverallDataContext).isLoading;
    const handleSubmit = useContext(OverallDataContext).handleSubmitButtonPress;
    const [inputReadOnly, setReadOnly] = useContext(OverallDataContext).inputReadOnly;

    const {
        tabPanels,
        tabNames,
        tagToTab,
        fpcatTabValue,
        selectedFootprintCategories
    } = useTabPanels();
    
    // Handle tab switching
    const handleTabForward = useCallback(() => {
        window.scrollTo(0, 0);
        setTabValue(prev => Math.min(prev + 1, Math.max(...Object.keys(tabNames).map(Number))));
    }, [tabNames]);
    const handleTabBack = useCallback(() => {
        window.scrollTo(0, 0);
        setTabValue(prev => Math.max(prev - 1, 0));
    }, []);

    useEffect(() => {
        // If calcStage is 3 (results stage) and loading has stopped, go to last tab (results tab)
        if (!isLoading) {
            if (calcStage === 3) setTabValue(Object.keys(tabNames).map(Number).at(-1));
            else if (calcStage === 2) setTabValue(fpcatTabValue);
        }
    }, [calcStage, isLoading])

    useEffect(() => {
        setTagToTabMap(prev => ({ ...prev, ...tagToTab}));
    }, [tagToTab])

    return (
        <>
        <FormProgressBar tabNames={tabNames} />

        {/* Form inputs and buttons */}
        <form id={formID} method="POST">
            <TabPanel value={tabValue} index={0}>
                <TabContent>
                    <IntroTab />
                </TabContent>
            </TabPanel>
            {tabPanels}

            <NavButtons
                calcStage={calcStage}
                setStage={setStage}
                tabValue={tabValue}
                setTabValue={setTabValue}
                fpcatTabValue={fpcatTabValue} 
                tabNames={tabNames} 
                selectedFootprintCategories={selectedFootprintCategories} 
                isLoading={isLoading}
                inputReadOnly={inputReadOnly}
                setReadOnly={setReadOnly}
                handleTabBack={handleTabBack}
                handleTabForward={handleTabForward}
                handleSubmit={handleSubmit}
            />
        </form>
        </>
    );
}

const NavButtons = ({ 
    calcStage, setStage,
    tabValue, setTabValue,
    fpcatTabValue, 
    tabNames, 
    selectedFootprintCategories,
    isLoading,
    inputReadOnly, setReadOnly,
    handleTabBack, handleTabForward, handleSubmit
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Button UI Flags
    const isFirstTab = tabValue === 0;
    const isLastTab = tabValue === Math.max(...Object.keys(tabNames).map(Number));
    const isFPCatTab = tabValue === fpcatTabValue;
    const isResultTab = calcStage === 3 && isLastTab;
    const hasSelectedCategories = Object.values(selectedFootprintCategories).some(Boolean);

    const showBack = !isFirstTab;
    const showReset = calcStage === 2 && tabValue <= fpcatTabValue;
    const showSave = (calcStage === 2 && tabValue > isFPCatTab) || calcStage === 3;
    const showEdit = calcStage === 3 && inputReadOnly && !isLastTab;
    const showCalcEdit = calcStage === 3 && !inputReadOnly && !isLastTab;
    const showCalc = isLastTab && (calcStage === 1 || (calcStage === 2 && hasSelectedCategories));
    const showNext = !isLastTab && !(isFPCatTab && calcStage === 1);
    const showNewSession = isResultTab;

    return (
        <Grid2 container sx={{ margin: '1em 0' }} spacing={1} className="hide-on-print">
            {/* Back Button */}
            <Grid2 xs={6} sm={3}>
                {showBack && (
                <Button className="pill_button mcc_button" disabled={isLoading} onClick={handleTabBack}>
                    <KeyboardArrowLeft /> Back
                </Button>
                )}
            </Grid2>

            {/* Center actions: Reset / Save / Edit */}
            <Grid2 xs={12} sm={6} order={{ xs: 3, sm: 2 }} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: { xs: 0, sm: 2 } }}>
                {showReset && (
                <Button className="pill_button secondary_button" disabled={isLoading} onClick={() => { setTabValue(0); setStage(1); }}>
                    <RotateLeft /> Reset Form
                </Button>
                )}
                
                {showSave && <SaveCalcModal />}

                {showEdit ? (
                <Button className="pill_button secondary_button" disabled={isLoading} onClick={() => setReadOnly(false)}>
                    <EditNote /> Edit Inputs
                </Button>
                )
                : showCalcEdit ? (
                <Button className="pill_button submit_button" disabled={isLoading} onClick={() => {setReadOnly(true); handleSubmit();}}>
                    Calculate <KeyboardDoubleArrowRight />
                </Button>
                )
                : null
                }
            </Grid2>

            {/* Forward button */}
            <Grid2 xs={6} sm={3} order={{ xs: 2, sm: 3 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {showNewSession && (
                <Button className="pill_button mcc_button" onClick={() => window.open(`#${process.env.REACT_APP_MCC_ROOT}/calculator`, '_blank')}>
                    <Calculate />&nbsp;New { !isSmallScreen && 'Session' }
                </Button>
                )}
                {showCalc && (
                <Button className="pill_button submit_button" disabled={isLoading} onClick={() => handleSubmit()}>
                    Calculate <KeyboardDoubleArrowRight />
                </Button>
                )}
                {showNext && (
                <Button className="pill_button mcc_button" disabled={isLoading} onClick={handleTabForward}>
                    Next <KeyboardArrowRight />
                </Button>
                )}
            </Grid2>
        </Grid2>
    )
}